import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, ArrowLeft, Eye, Download, Search, Pencil, Trash2, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const AdminBookings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [proofObjectUrl, setProofObjectUrl] = useState('');
  const [proofLoading, setProofLoading] = useState(false);
  const [proofLoadError, setProofLoadError] = useState('');
  const proofObjectUrlRef = useRef('');
  const [hourlyPricingRules, setHourlyPricingRules] = useState([]);
  const [venueHourlyPricingRules, setVenueHourlyPricingRules] = useState({});
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  
  // Edit state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [targetDate, setTargetDate] = useState('');
  const [loadingSlots, setLoadingSlots] = useState(false);

  const localDateISO = () => {
    const now = new Date();
    const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
    return local.toISOString().split('T')[0];
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    
    if (!token || !userStr) {
      const isKasirPath = window.location.pathname.startsWith('/kasir');
      navigate(isKasirPath ? '/kasir/login' : '/admin/login');
      return;
    }
    
    try {
      const user = JSON.parse(userStr);
      if (user.role !== 'admin' && user.role !== 'kasir') {
        const isKasirPath = window.location.pathname.startsWith('/kasir');
        navigate(isKasirPath ? '/kasir/login' : '/admin/login');
        return;
      }
      setIsAdmin(user.role === 'admin');
    } catch (error) {
      const isKasirPath = window.location.pathname.startsWith('/kasir');
      navigate(isKasirPath ? '/kasir/login' : '/admin/login');
      return;
    }
  }, [navigate]);

  useEffect(() => {
    loadHourlyPricingRules();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const prev = proofObjectUrlRef.current;
    const rawProofUrl = String(selectedBooking?.payment?.proof_image_url || '').trim();
    const isRemoteProof = rawProofUrl.startsWith('http://') || rawProofUrl.startsWith('https://');
    if (!showDialog || !selectedBooking?.payment?.id || !rawProofUrl) {
      if (prev) URL.revokeObjectURL(prev);
      setProofObjectUrl('');
      proofObjectUrlRef.current = '';
      setProofLoading(false);
      setProofLoadError('');
      return () => {};
    }

    if (isRemoteProof) {
      if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      setProofObjectUrl(rawProofUrl);
      proofObjectUrlRef.current = rawProofUrl;
      setProofLoading(false);
      setProofLoadError('');
      return () => {};
    }

    setProofLoading(true);
    setProofLoadError('');
    api
      .get(`/admin/payments/${selectedBooking.payment.id}/proof`, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        const next = URL.createObjectURL(res.data);
        if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
        setProofObjectUrl(next);
        proofObjectUrlRef.current = next;
      })
      .catch((err) => {
        if (cancelled) return;
        if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
        setProofObjectUrl('');
        proofObjectUrlRef.current = '';
        setProofLoadError(err?.response?.data?.detail || 'Gagal memuat bukti pembayaran');
      })
      .finally(() => {
        if (cancelled) return;
        setProofLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [showDialog, selectedBooking?.payment?.id, selectedBooking?.payment?.proof_image_url]);

  const loadHourlyPricingRules = async () => {
    try {
      const response = await api.get('/settings/public');
      const rawRulesMap = response.data?.venue_hourly_pricing_rules;
      const rawRules = response.data?.hourly_pricing_rules;
      if (rawRulesMap) {
        try {
          const parsedMap = JSON.parse(rawRulesMap);
          setVenueHourlyPricingRules(parsedMap && typeof parsedMap === 'object' ? parsedMap : {});
        } catch {
          setVenueHourlyPricingRules({});
        }
      } else {
        setVenueHourlyPricingRules({});
      }

      if (!rawRules) {
        setHourlyPricingRules([]);
      } else {
        const parsed = JSON.parse(rawRules);
        setHourlyPricingRules(Array.isArray(parsed) ? parsed : []);
      }
    } catch {
      setHourlyPricingRules([]);
      setVenueHourlyPricingRules({});
    }
  };

  const getDayType = (dateStr) => {
    const dt = dateStr ? new Date(`${dateStr}T00:00:00`) : null;
    if (!dt || Number.isNaN(dt.getTime())) return 'weekday';
    const day = dt.getDay();
    return day === 0 || day === 6 ? 'weekend' : 'weekday';
  };

  const getHourlyRate = (rulesList, hour, basePricePerHour, dayType) => {
    const rules = Array.isArray(rulesList) ? rulesList : [];
    const day = String(dayType || 'all').toLowerCase();
    for (const rule of rules) {
      const startHour = Number(rule?.start_hour ?? rule?.startHour);
      const endHour = Number(rule?.end_hour ?? rule?.endHour);
      const pricePerHour = Number(rule?.price_per_hour ?? rule?.pricePerHour);
      const ruleDayType = String(rule?.day_type ?? rule?.dayType ?? 'all').toLowerCase();
      if (!Number.isInteger(startHour) || !Number.isInteger(endHour)) continue;
      if (!Number.isFinite(pricePerHour) || pricePerHour < 0) continue;
      if (ruleDayType === day && hour >= startHour && hour < endHour) return pricePerHour;
    }
    for (const rule of rules) {
      const startHour = Number(rule?.start_hour ?? rule?.startHour);
      const endHour = Number(rule?.end_hour ?? rule?.endHour);
      const pricePerHour = Number(rule?.price_per_hour ?? rule?.pricePerHour);
      const ruleDayType = String(rule?.day_type ?? rule?.dayType ?? 'all').toLowerCase();
      if (!Number.isInteger(startHour) || !Number.isInteger(endHour)) continue;
      if (!Number.isFinite(pricePerHour) || pricePerHour < 0) continue;
      if (ruleDayType === 'all' && hour >= startHour && hour < endHour) return pricePerHour;
    }
    return basePricePerHour;
  };

  const calculateTotalAmount = (venuePricePerHour, timeSlots, bookingDate, venueId) => {
    const slots = Array.isArray(timeSlots)
      ? timeSlots
      : (typeof timeSlots === 'string' && timeSlots.trim() ? [timeSlots.trim()] : []);
    if (slots.length === 0) return venuePricePerHour || 0;
    const dayType = getDayType(bookingDate);
    const venueRules = venueId && venueHourlyPricingRules ? venueHourlyPricingRules[venueId] : null;
    const rules = Array.isArray(venueRules) ? venueRules : hourlyPricingRules;
    return slots.reduce((sum, slot) => {
      const match = typeof slot === 'string' ? slot.match(/^(\d{1,2}):/) : null;
      const hour = match ? Number(match[1]) : null;
      const rate = Number.isInteger(hour)
        ? getHourlyRate(rules, hour, venuePricePerHour || 0, dayType)
        : (venuePricePerHour || 0);
      return sum + rate;
    }, 0);
  };

  const formatDateTime = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('id-ID', { timeZone: 'Asia/Jakarta' });
  };

  const loadBookings = async (query = '') => {
    try {
      const response = await api.get('/admin/bookings', {
        params: { search: query }
      });
      setBookings(response.data);
    } catch (error) {
      toast.error('Gagal memuat data booking');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      loadBookings(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleVerifyPayment = async (paymentId, status) => {
    const user = JSON.parse(localStorage.getItem('user'));
    
    try {
      await api.put(`/admin/payments/${paymentId}/verify`, {
        status,
        admin_id: user.id
      });
      toast.success(`Pembayaran ${status === 'verified' ? 'disetujui' : 'ditolak'}`);
      setShowDialog(false);
      loadBookings(searchTerm);
    } catch (error) {
      toast.error('Gagal memverifikasi pembayaran');
    }
  };

  const isBookingPaid = (bookingWithDetails) => bookingWithDetails?.payment?.status === 'verified';

  const isBookingUnpaid = (bookingWithDetails) => !isBookingPaid(bookingWithDetails);

  const isBookingPayable = (bookingWithDetails) => !['cancelled', 'expired', 'expire'].includes(bookingWithDetails?.booking?.status);

  const getPaymentMethodLabel = (payment) => {
    const method = String(payment?.method || '').toLowerCase();
    if (!method) return '-';
    if (method === 'cash') return 'Tunai';
    if (method === 'qris') return 'QRIS';
    if (method.startsWith('transfer')) return 'Transfer';
    return payment?.method || '-';
  };

  const getPaymentSummaryLabel = (bookingWithDetails) => {
    if (!isBookingPayable(bookingWithDetails)) return '-';
    if (isBookingPaid(bookingWithDetails)) return 'Lunas';
    return 'Belum Lunas';
  };

  const queryParams = new URLSearchParams(location.search || '');
  const unpaidOnly = queryParams.get('payment') === 'unpaid' || queryParams.get('unpaid') === '1';
  const displayedBookings = unpaidOnly
    ? bookings.filter((b) => isBookingPayable(b) && isBookingUnpaid(b))
    : bookings;

  const handleSettleBooking = async (bookingWithDetails) => {
    const bookingId = bookingWithDetails?.booking?.id;
    if (!bookingId) return;

    const computedAny =
      bookingWithDetails?.payment?.total_amount ??
      bookingWithDetails?.payment?.totalAmount ??
      bookingWithDetails?.computed_total_amount ??
      bookingWithDetails?.computedTotalAmount;
    const computed = Number(computedAny);
    const totalAmount = Number.isFinite(computed)
      ? computed
      : calculateTotalAmount(
          bookingWithDetails?.venue?.price_per_hour || 0,
          bookingWithDetails?.booking?.time_slots,
          bookingWithDetails?.booking?.booking_date,
          bookingWithDetails?.booking?.venue_id
        );

    const ok = window.confirm(
      `Konfirmasi pelunasan booking ${String(bookingId).slice(0, 8)} sebesar Rp ${Number(totalAmount || 0).toLocaleString('id-ID')}?`
    );
    if (!ok) return;

    const user = JSON.parse(localStorage.getItem('user'));

    try {
      if (bookingWithDetails?.payment?.id) {
        await api.put(`/admin/payments/${bookingWithDetails.payment.id}/verify`, {
          status: 'verified',
          admin_id: user.id
        });
      } else {
        const paymentResponse = await api.post('/payments', {
          booking_id: bookingId,
          method: 'cash',
          amount: totalAmount
        });

        await api.put(`/admin/payments/${paymentResponse.data.id}/verify`, {
          status: 'verified',
          admin_id: user.id
        });
      }

      toast.success('Pelunasan berhasil');
      loadBookings(searchTerm);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Gagal melakukan pelunasan');
    }
  };

  const handleApproveCancel = async (bookingId) => {
    try {
      await api.post(`/admin/bookings/${bookingId}/cancel-approve`);
      toast.success('Cancel booking disetujui');
      loadBookings(searchTerm);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Gagal approve cancel');
    }
  };

  const handleRejectCancel = async (bookingId) => {
    try {
      await api.post(`/admin/bookings/${bookingId}/cancel-reject`);
      toast.success('Cancel booking ditolak');
      loadBookings(searchTerm);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Gagal reject cancel');
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    const ok = window.confirm('Hapus booking ini?');
    if (!ok) return;
    try {
      await api.delete(`/admin/bookings/${bookingId}`);
      toast.success('Booking berhasil dihapus');
      loadBookings(searchTerm);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Gagal hapus booking');
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/admin/export/transactions', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `zero_futsal_transactions_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Laporan berhasil didownload');
    } catch (error) {
      toast.error('Gagal export laporan');
    }
  };

  const handleEditClick = async (bookingWithDetails) => {
    setEditingBooking(bookingWithDetails);
    // Ensure time_slots is array
    const currentSlots = Array.isArray(bookingWithDetails.booking.time_slots) 
      ? bookingWithDetails.booking.time_slots 
      : (bookingWithDetails.booking.time_slot ? [bookingWithDetails.booking.time_slot] : []);
      
    setSelectedSlots(currentSlots);
    setTargetDate(bookingWithDetails.booking.booking_date);
    setShowEditDialog(true);
    fetchAvailableSlots(bookingWithDetails.booking.venue_id, bookingWithDetails.booking.booking_date);
  };

  const fetchAvailableSlots = async (venueId, date) => {
    setLoadingSlots(true);
    try {
      const response = await api.get('/bookings/slots', {
        params: {
          venue_id: venueId,
          date: date
        }
      });
      setAvailableSlots(response.data);
    } catch (error) {
      toast.error('Gagal memuat slot');
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleDateChange = (newDate) => {
    setTargetDate(newDate);
    // When date changes, we should clear selected slots IF they are not valid anymore?
    // Or keep them and let user decide?
    // Standard behavior: clear slots or validate them against new availability
    // Here we will keep them but user must check availability
    setSelectedSlots([]); // Reset selection on date change is safer
    if (editingBooking) {
      fetchAvailableSlots(editingBooking.booking.venue_id, newDate);
    }
  };

  const toggleSlot = (time) => {
    // Check if slot is available (or owned by current booking)
    const slotInfo = availableSlots.find(s => s.time === time);
    // Is owned ONLY if we are on the original date
    const isSameDate = targetDate === editingBooking.booking.booking_date;
    const isOwned = isSameDate && editingBooking.booking.time_slots?.includes(time);
    
    if (!slotInfo?.available && !isOwned) {
      return; // Cannot select unavailable slots
    }

    if (selectedSlots.includes(time)) {
      // Unselect - Check if we are reducing slots below original count
      const originalCount = editingBooking.booking.time_slots?.length || 0;
      if (selectedSlots.length - 1 < originalCount) {
         // UI Restriction: Cannot reduce slots below original count
         // But user might want to swap slots? 
         // "hanya bisa tambah jam tidak bisa mengurangi slot" 
         // Implementation: Just check final count on save? 
         // Or strictly prevent unselecting original slots? 
         // Let's allow unselecting for now, but validate on save/UI warning.
         // Actually, if they unselect an original slot, they are reducing.
         // Let's allow unselecting, but show warning/error if count is low.
      }
      setSelectedSlots(selectedSlots.filter(s => s !== time));
    } else {
      setSelectedSlots([...selectedSlots, time].sort());
    }
  };

  const handleSaveEdit = async () => {
    const originalCount = editingBooking.booking.time_slots?.length || 0;
    if (selectedSlots.length < originalCount) {
       toast.error('Tidak bisa mengurangi slot (hanya bisa menambah)');
       return;
    }
    
    try {
       await api.put(
          isAdmin
            ? `/admin/bookings/${editingBooking.booking.id}`
            : `/kasir/bookings/${editingBooking.booking.id}/slots`,
          {
          time_slots: selectedSlots,
          booking_date: targetDate
       });
       toast.success('Jadwal berhasil diupdate');
       setShowEditDialog(false);
       loadBookings(searchTerm);
    } catch (err) {
       toast.error(err.response?.data?.detail || 'Gagal update booking');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      unpaid: { text: 'Belum Lunas', class: 'bg-red-500/15 text-red-300 border-red-500/30 shadow-[0_0_18px_rgba(239,68,68,0.10)]' },
      pending: { text: 'Pending', class: 'bg-secondary/15 text-secondary border-secondary/30 shadow-[0_0_18px_rgba(250,204,21,0.10)]' },
      confirmed: { text: 'Confirmed', class: 'bg-primary/15 text-primary border-primary/30 shadow-[0_0_18px_rgba(206,255,5,0.12)]' },
      cancelled: { text: 'Cancelled', class: 'bg-red-500/15 text-red-300 border-red-500/30 shadow-[0_0_18px_rgba(239,68,68,0.10)]' },
      cancel_requested: { text: 'Cancel Req', class: 'bg-orange-500/15 text-orange-300 border-orange-500/30 shadow-[0_0_18px_rgba(249,115,22,0.10)]' },
      expired: { text: 'Expire', class: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30 shadow-[0_0_18px_rgba(113,113,122,0.10)]' },
      expire: { text: 'Expire', class: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30 shadow-[0_0_18px_rgba(113,113,122,0.10)]' },
      verified: { text: 'Verified', class: 'bg-primary/15 text-primary border-primary/30 shadow-[0_0_18px_rgba(206,255,5,0.12)]' },
      rejected: { text: 'Rejected', class: 'bg-red-500/15 text-red-300 border-red-500/30 shadow-[0_0_18px_rgba(239,68,68,0.10)]' }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase border ${badge.class}`}>
        {badge.text}
      </span>
    );
  };

  // Calculate additional cost
  const calculateAdditionalCost = () => {
    if (!editingBooking) return 0;
    // If date changed, maybe all slots are "new"?
    // Or we just diff the count?
    // Requirement: "sisa pembayaran (Tambahan)"
    // Usually rescheduling is free if count is same, or just pay difference if more slots.
    // If date changed, we still compare count.
    const basePricePerHour = editingBooking.venue?.price_per_hour || 0;
    const venueId = editingBooking.booking?.venue_id;
    const originalTotal = calculateTotalAmount(basePricePerHour, editingBooking.booking.time_slots, editingBooking.booking.booking_date, venueId);
    const newTotal = calculateTotalAmount(basePricePerHour, selectedSlots, targetDate, venueId);
    return Math.max(0, newTotal - originalTotal);
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-white/10 bg-background-paper/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to={isAdmin ? "/admin/dashboard" : "/kasir/dashboard"} className="flex items-center space-x-3">
              <img src="/zero-logo.png" alt="Logo" className="w-10 h-10 object-contain rounded-md" />
              <span className="text-2xl font-heading font-black uppercase text-primary glow-text">{isAdmin ? 'Admin Panel' : 'Kasir Panel'}</span>
            </Link>
            <Link to={isAdmin ? "/admin/dashboard" : "/kasir/dashboard"} className="text-white/60 hover:text-primary flex items-center gap-2">
              <ArrowLeft className="w-5 h-5" />
              <span>Dashboard</span>
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <h1 className="text-4xl font-heading font-black uppercase text-white">Kelola Booking</h1>
            {isAdmin && (
              <button
                data-testid="export-transactions-btn"
                onClick={handleExport}
                className="bg-primary text-black px-8 py-3 rounded-full font-bold uppercase tracking-wider hover:bg-primary-hover transition-all flex items-center gap-2 shadow-[0_0_0_1px_rgba(206,255,5,0.35),0_0_24px_rgba(206,255,5,0.12)]"
              >
                <Download className="w-5 h-5" />
                Export Laporan
              </button>
            )}
          </div>

          {/* Search Bar */}
          <div className="mb-6 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-white/40" />
            </div>
            <input
              type="text"
              placeholder="Cari booking (Nama, ID, Lapangan, No HP)..."
              className="w-full bg-black/30 border border-white/10 text-white rounded-2xl pl-10 pr-4 py-3 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all placeholder:text-white/40"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : displayedBookings.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-zinc-500 text-lg">
                {unpaidOnly ? 'Tidak ada booking yang belum lunas' : searchTerm ? 'Tidak ada booking yang cocok' : 'Belum ada booking'}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {displayedBookings.map((item) => (
                <div
                  key={item.booking.id}
                  data-testid={`booking-${item.booking.id}`}
                  className={`glass glow-border rounded-3xl p-6 ${isBookingUnpaid(item) ? 'ring-1 ring-red-500/25' : ''}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-white mb-1">{item.venue?.name}</h3>
                      <p className="text-sm text-white/50 font-mono">Booking ID: {item.booking.id.slice(0, 8)}</p>
                    </div>
                    <div className="flex gap-2">
                      {getStatusBadge(item.booking.status)}
                      {isBookingUnpaid(item) && getStatusBadge('unpaid')}
                      {item.payment && getStatusBadge(item.payment.status)}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    <div>
                      <div className="text-xs text-zinc-400">Customer</div>
                      <div className="text-white font-bold">{item.booking.customer_name}</div>
                      <div className="text-xs text-zinc-500">{item.booking.customer_phone}</div>
                    </div>
                    <div>
                      <div className="text-xs text-zinc-400">Tanggal & Jam</div>
                      <div className="text-white font-bold">{item.booking.booking_date}</div>
                      <div className="text-xs text-zinc-500">
                        {Array.isArray(item.booking.time_slots)
                          ? item.booking.time_slots.length > 1
                            ? `${item.booking.time_slots.length} slot`
                            : item.booking.time_slots[0]
                          : item.booking.time_slot || '-'}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-zinc-400">Dibuat</div>
                      <div className="text-white font-bold">{formatDateTime(item.booking.created_at)}</div>
                      <div className="text-xs text-zinc-500">Expire: {formatDateTime(item.booking.locked_until)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-zinc-400">Total Harga</div>
                      <div className="text-white font-bold">
                        Rp {Number(
                          item?.payment?.total_amount ??
                            item?.payment?.totalAmount ??
                            item?.computed_total_amount ??
                            item?.computedTotalAmount ??
                            calculateTotalAmount(item.venue?.price_per_hour || 0, item.booking.time_slots, item.booking.booking_date, item.booking.venue_id)
                        ).toLocaleString('id-ID')}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-zinc-400">Metode Bayar</div>
                      <div className="text-white font-bold uppercase">
                        {getPaymentMethodLabel(item.payment)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-zinc-400">Pembayaran</div>
                      <div className={`text-white font-bold ${isBookingPayable(item) && isBookingUnpaid(item) ? 'text-red-300' : ''}`}>
                        {getPaymentSummaryLabel(item)}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    <button
                      data-testid={`view-booking-${item.booking.id}`}
                      onClick={() => {
                        setSelectedBooking(item);
                        setShowDialog(true);
                      }}
                      className="bg-transparent border-2 border-primary text-primary hover:bg-primary/10 px-4 py-2 rounded-md font-bold uppercase text-sm transition-all flex items-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      Detail
                    </button>
                    
                    <button
                      onClick={() => handleEditClick(item)}
                      className="bg-zinc-800 text-zinc-300 hover:bg-zinc-700 px-4 py-2 rounded-md font-bold uppercase text-sm transition-all flex items-center gap-2"
                    >
                      <Pencil className="w-4 h-4" />
                      Edit Jadwal
                    </button>

                    {isBookingUnpaid(item) && !['cancelled', 'expired', 'expire'].includes(item.booking?.status) && (
                      <button
                        onClick={() => handleSettleBooking(item)}
                        className="bg-secondary text-black hover:bg-secondary-hover px-4 py-2 rounded-md font-bold uppercase text-sm transition-all flex items-center gap-2"
                      >
                        <DollarSign className="w-4 h-4" />
                        Pelunasan
                      </button>
                    )}

                    {isAdmin && item.booking?.status === 'cancel_requested' && (
                      <>
                        <button
                          onClick={() => handleApproveCancel(item.booking.id)}
                          className="bg-primary text-black hover:bg-primary-hover px-4 py-2 rounded-md font-bold uppercase text-sm transition-all flex items-center gap-2"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Approve Cancel
                        </button>
                        <button
                          onClick={() => handleRejectCancel(item.booking.id)}
                          className="bg-zinc-800 text-zinc-300 hover:bg-zinc-700 px-4 py-2 rounded-md font-bold uppercase text-sm transition-all flex items-center gap-2"
                        >
                          <XCircle className="w-4 h-4" />
                          Reject Cancel
                        </button>
                      </>
                    )}

                    {item.payment?.status === 'pending' && (
                      <>
                        <button
                          data-testid={`approve-payment-${item.payment.id}`}
                          onClick={() => handleVerifyPayment(item.payment.id, 'verified')}
                          className="bg-primary text-black hover:bg-primary-hover px-4 py-2 rounded-md font-bold uppercase text-sm transition-all flex items-center gap-2"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Setujui
                        </button>
                        <button
                          data-testid={`reject-payment-${item.payment.id}`}
                          onClick={() => handleVerifyPayment(item.payment.id, 'rejected')}
                          className="bg-red-600 text-white hover:bg-red-700 px-4 py-2 rounded-md font-bold uppercase text-sm transition-all flex items-center gap-2"
                        >
                          <XCircle className="w-4 h-4" />
                          Tolak
                        </button>
                      </>
                    )}

                    {isAdmin && (
                      <button
                        onClick={() => handleDeleteBooking(item.booking.id)}
                        className="bg-zinc-800 text-red-300 hover:bg-zinc-700 px-4 py-2 rounded-md font-bold uppercase text-sm transition-all flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Hapus Booking
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Detail Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading font-bold uppercase">Detail Booking</DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-zinc-400">Lapangan</div>
                  <div className="text-white font-bold">{selectedBooking.venue?.name}</div>
                </div>
                <div>
                  <div className="text-sm text-zinc-400">Tipe</div>
                  <div className="text-white font-bold">{selectedBooking.venue?.type}</div>
                </div>
                <div>
                  <div className="text-sm text-zinc-400">Customer</div>
                  <div className="text-white font-bold">{selectedBooking.booking.customer_name}</div>
                </div>
                <div>
                  <div className="text-sm text-zinc-400">No. HP</div>
                  <div className="text-white font-bold">{selectedBooking.booking.customer_phone}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-sm text-zinc-400">Alamat</div>
                  <div className="text-white font-bold">{selectedBooking.booking.customer_address}</div>
                </div>
                <div>
                  <div className="text-sm text-zinc-400">Tanggal</div>
                  <div className="text-white font-bold">{selectedBooking.booking.booking_date}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-sm text-zinc-400">Jam</div>
                  <div className="text-white font-bold">
                    {Array.isArray(selectedBooking.booking.time_slots)
                      ? selectedBooking.booking.time_slots.join(', ')
                      : selectedBooking.booking.time_slot || '-'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-zinc-400">Total Harga</div>
                  <div className="text-white font-bold">
                    Rp {Number(
                      selectedBooking?.payment?.total_amount ??
                        selectedBooking?.payment?.totalAmount ??
                        selectedBooking?.computed_total_amount ??
                        selectedBooking?.computedTotalAmount ??
                        calculateTotalAmount(selectedBooking.venue?.price_per_hour || 0, selectedBooking.booking.time_slots, selectedBooking.booking.booking_date, selectedBooking.booking.venue_id)
                    ).toLocaleString('id-ID')}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-zinc-400">Metode Bayar</div>
                  <div className="text-white font-bold uppercase">
                    {getPaymentMethodLabel(selectedBooking.payment)}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-zinc-400">Status Pembayaran</div>
                  <div className={`text-white font-bold ${isBookingPayable(selectedBooking) && isBookingUnpaid(selectedBooking) ? 'text-red-300' : ''}`}>
                    {getPaymentSummaryLabel(selectedBooking)}
                  </div>
                </div>
              </div>

              {selectedBooking.payment?.proof_image_url && (
                <div>
                  <div className="text-sm text-zinc-400 mb-2">Bukti Pembayaran</div>
                  {proofLoading ? (
                    <div className="text-sm text-zinc-500">Memuat bukti pembayaran...</div>
                  ) : proofObjectUrl ? (
                    <img
                      src={proofObjectUrl}
                      alt="Bukti pembayaran"
                      className="w-full max-h-96 object-contain rounded border border-zinc-700"
                    />
                  ) : (
                    <div className="text-sm text-red-300">{proofLoadError || 'Bukti pembayaran tidak tersedia'}</div>
                  )}
                </div>
              )}

              {selectedBooking.payment?.status === 'pending' && (
                <div className="flex gap-4 pt-4">
                  <button
                    onClick={() => handleVerifyPayment(selectedBooking.payment.id, 'verified')}
                    className="flex-1 bg-primary text-black hover:bg-primary-hover px-6 py-3 rounded-md font-bold uppercase transition-all flex items-center justify-center gap-2"
                  >
                    <CheckCircle className="w-5 h-5" />
                    Setujui Pembayaran
                  </button>
                  <button
                    onClick={() => handleVerifyPayment(selectedBooking.payment.id, 'rejected')}
                    className="flex-1 bg-red-600 text-white hover:bg-red-700 px-6 py-3 rounded-md font-bold uppercase transition-all flex items-center justify-center gap-2"
                  >
                    <XCircle className="w-5 h-5" />
                    Tolak Pembayaran
                  </button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Slots Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading font-bold uppercase">Edit Jadwal Booking</DialogTitle>
          </DialogHeader>
          
          {editingBooking && (
            <div className="space-y-6">
              <div className="bg-zinc-800/50 p-4 rounded-lg">
                 <h4 className="font-bold text-white mb-2">{editingBooking.venue?.name}</h4>
                 <div className="flex items-center gap-4 mb-2">
                   <div className="flex-1">
                     <label className="text-xs text-zinc-400 block mb-1">Tanggal Booking</label>
                     <input 
                       type="date"
                       value={targetDate}
                       onChange={(e) => handleDateChange(e.target.value)}
                       min={localDateISO()}
                       className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-white focus:outline-none focus:border-primary"
                     />
                   </div>
                 </div>
                 <p className="text-sm text-zinc-400">Pilih slot waktu. Anda tidak dapat mengurangi jumlah slot dari booking awal.</p>
              </div>

              {loadingSlots ? (
                <div className="text-center py-8">
                  <div className="inline-block w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {availableSlots.map((slot) => {
                    const isSelected = selectedSlots.includes(slot.time);
                    // Check ownership on the specific date
                    const isSameDate = targetDate === editingBooking.booking.booking_date;
                    const isOriginallyBooked = isSameDate && editingBooking.booking.time_slots?.includes(slot.time);
                    const isAvailable = slot.available || isOriginallyBooked;
                    
                    return (
                      <button
                        key={slot.time}
                        disabled={!isAvailable}
                        onClick={() => toggleSlot(slot.time)}
                        className={`
                          p-2 text-sm rounded-md font-medium border transition-all
                          ${isSelected 
                            ? 'bg-primary text-black border-primary' 
                            : isAvailable
                              ? 'bg-zinc-800 text-zinc-300 border-zinc-700 hover:border-primary/50'
                              : 'bg-zinc-900/50 text-zinc-600 border-zinc-800 cursor-not-allowed'}
                        `}
                      >
                        {slot.time}
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="border-t border-zinc-800 pt-4 mt-4">
                 <div className="flex justify-between items-center mb-4">
                    <span className="text-zinc-400">Slot Dipilih:</span>
                    <span className="font-bold text-white">{selectedSlots.length} slot</span>
                 </div>
                 <div className="flex justify-between items-center mb-4">
                    <span className="text-zinc-400">Sisa Pembayaran (Tambahan):</span>
                    <span className="font-bold text-primary text-xl">
                       Rp {calculateAdditionalCost().toLocaleString('id-ID')}
                    </span>
                 </div>
                 
                 <div className="flex gap-4">
                    <button
                       onClick={() => setShowEditDialog(false)}
                       className="flex-1 bg-transparent border border-zinc-700 text-white hover:bg-zinc-800 px-4 py-3 rounded-md font-bold uppercase"
                    >
                       Batal
                    </button>
                    <button
                       onClick={handleSaveEdit}
                       disabled={selectedSlots.length < (editingBooking.booking.time_slots?.length || 0)}
                       className="flex-1 bg-primary text-black hover:bg-primary-hover px-4 py-3 rounded-md font-bold uppercase disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                       Simpan Perubahan
                    </button>
                 </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBookings;
